import { Alert, Snackbar } from "@mui/material";
import { ReactElement, useCallback, useState } from "react";

/**
 * Feedback for a save that the server refused.
 *
 * Row edits and form fields used to fail silently (`console.error` only), so a
 * rejected save looked exactly like a value that "didn't register": the cell or
 * field snapped back to the server value with no explanation.
 *
 * @returns {object} `reportSaveError` to call from a catch block, and
 * `saveErrorSnackbar` to render.
 */
export function useSaveError(): {
  reportSaveError: (error: unknown) => void;
  saveErrorSnackbar: ReactElement;
} {
  const [message, setMessage] = useState<string>();

  const reportSaveError = useCallback((error: unknown) => {
    console.error("Save failed", error);
    setMessage(
      "That change could not be saved and was reverted. Please try again."
    );
  }, []);

  const saveErrorSnackbar = (
    <Snackbar
      open={Boolean(message)}
      autoHideDuration={6000}
      onClose={() => setMessage(undefined)}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert severity="error" onClose={() => setMessage(undefined)}>
        {message}
      </Alert>
    </Snackbar>
  );

  return { reportSaveError, saveErrorSnackbar };
}

export default useSaveError;
